import {
  Paper,
  Skeleton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { PropsWithChildren } from 'react';
import { Collection } from '../../../services/entities/impl/blfApi/schemas/collection';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';

type Props = {
  collections: Collection[] | undefined;
  onRowClick: (row: Collection) => void;
  loading?: boolean;
};

export const CollectionsTable = ({
  collections,
  onRowClick,
  loading,
}: Props) => {
  const { t } = useTranslation();

  return (
    <CollectionsTableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('Scan date')}</TableCell>
            <TableCell>{`${t('Collection')} ID`}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <SkeletonTableRows />
          ) : (
            collections?.map((collection) => (
              <TableRow
                key={collection.id}
                hover
                onClick={() => onRowClick(collection)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  {format(parseISO(collection.collectionDate), 'M/dd')}
                </TableCell>
                <TableCell>
                  <Typography variant='body2' color='textSecondary'>
                    {collection.id}
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </CollectionsTableContainer>
  );
};

const CollectionsTableContainer = styled((props: PropsWithChildren) => (
  <TableContainer {...props} component={Paper} />
))(({ theme }) => ({
  paddingBottom: theme.spacing(2),
}));

const SkeletonTableRows = () => {
  const theme = useTheme();
  const extraLarge = useMediaQuery(theme.breakpoints.up('xl'));
  const large = useMediaQuery(theme.breakpoints.up('lg'));
  const medium = useMediaQuery(theme.breakpoints.up('md'));

  const skeletonCount = extraLarge ? 15 : large ? 10 : medium ? 8 : 5;
  return (
    <>
      {Array.from({ length: skeletonCount }).map((_, index) => (
        <TableRow key={`skeleton-row-${index}`}>
          <TableCell>
            <Skeleton variant='text' />
          </TableCell>
          <TableCell>
            <Skeleton variant='text' />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
};
